import { useMemo, useState } from 'react';
import { Navigate, useParams } from 'react-router-dom';
import { useTranslation } from 'react-i18next';
import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query';
import { pagesApi } from './api';
import { SectionBuilder } from './SectionBuilder';
import { config } from '@/lib/config';
import { keys } from '@/lib/query/keys';
import { useAuth } from '@/lib/auth/useAuth';
import { useGate } from '@/lib/rbac/Gate';
import { useApiError } from '@/lib/query/useApiError';
import { useToast } from '@/lib/ui';
import { slugify } from '@/lib/slug';
import { diffPatch } from '@/lib/forms/diff';
import { DataView } from '@/lib/ui/states';
import { PageHeader } from '@/lib/ui/Typography';
import { Tabs } from '@/lib/ui/Tabs';
import { Card } from '@/lib/ui/Card';
import { TextInput } from '@/lib/ui/Field';
import { I18nField } from '@/lib/forms/widgets/I18nField';
import { SeoPanel, EMPTY_SEO } from '@/features/shared/SeoPanel';
import { PublishControls } from '@/features/shared/PublishControls';
import { PreviewButton } from '@/features/preview/preview';
import { RevisionsTab } from '@/features/revisions/RevisionsTab';
import { PermissionButton } from '@/lib/ui/Button';
import type { I18n, Page, SEO } from '@/lib/api/types';

const ENTITY_TYPE = 'page';

export function PageEditorPage() {
  const { id } = useParams();
  const isNew = id === 'new' || !id;
  const qc = useQueryClient();

  const query = useQuery({
    queryKey: keys.pages.detail(id ?? 'new'),
    queryFn: () => pagesApi.get(id!),
    enabled: !isNew,
  });

  // Pages are an edit-only set — creation is disabled, so /pages/new is not a valid route.
  if (isNew) return <Navigate to="/pages" replace />;

  return (
    <DataView query={query}>
      {(page) => (
        <Editor
          original={page}
          onSaved={() => qc.invalidateQueries({ queryKey: keys.pages.detail(page.id) })}
        />
      )}
    </DataView>
  );
}

function Editor({
  original,
  onSaved,
}: {
  original: Page | null;
  onSaved: (page: Page) => void;
}) {
  const qc = useQueryClient();
  const { t } = useTranslation();
  const toast = useToast();
  const onError = useApiError();
  const { isTranslateOnly } = useAuth();
  const { allowed: canWrite } = useGate('content:write');

  const isNew = !original;
  const [title, setTitle] = useState<I18n>(original?.title ?? { en: '', ar: '' });
  const [slug, setSlug] = useState(original?.slug ?? '');
  const [slugTouched, setSlugTouched] = useState(!!original?.slug);
  const [key, setKey] = useState(original?.key ?? '');
  const [seo, setSeo] = useState<SEO>(original?.seo ?? EMPTY_SEO);
  const [errors, setErrors] = useState<Record<string, string>>({});

  const effectiveSlug = useMemo(
    () => (!slugTouched ? slugify(title.en) : slug),
    [slugTouched, title.en, slug],
  );

  const buildBody = () => ({
    title,
    slug: effectiveSlug,
    key,
    seo,
  });

  const save = useMutation({
    mutationFn: () => {
      setErrors({});
      if (isNew) return pagesApi.create(buildBody());
      const prev = {
        title: original.title,
        slug: original.slug,
        key: original.key,
        seo: original.seo,
      };
      return pagesApi.update(original.id, diffPatch(prev, buildBody()));
    },
    onSuccess: (page) => {
      void qc.invalidateQueries({ queryKey: keys.pages.all() });
      void qc.invalidateQueries({ queryKey: keys.dashboard() });
      toast.success(isNew ? t('pages.createdToast') : t('pages.savedToast'), t('pages.siteUpdateSoon'));
      onSaved(page);
    },
    onError: (e) => onError(e, { onValidation: setErrors }),
  });

  const publish = useMutation({
    mutationFn: (scheduledAt?: string) => pagesApi.publish(original!.id, scheduledAt),
    onSuccess: () => {
      void qc.invalidateQueries({ queryKey: keys.pages.all() });
      void qc.invalidateQueries({ queryKey: keys.dashboard() });
      toast.success(t('pages.publicationUpdatedToast'), t('pages.siteUpdateSoon'));
    },
    onError,
  });
  const unpublish = useMutation({
    mutationFn: () => pagesApi.unpublish(original!.id),
    onSuccess: () => {
      void qc.invalidateQueries({ queryKey: keys.pages.all() });
      void qc.invalidateQueries({ queryKey: keys.dashboard() });
      toast.success(t('pages.unpublishedToast'));
    },
    onError,
  });

  const identityForm = (
    <Card>
      <div className="flex flex-col gap-md">
        <I18nField
          label={t('common.title')}
          value={title}
          onChange={setTitle}
          required
          error={errors['title.en'] ?? errors.title}
          translateOnly={isTranslateOnly}
        />
        <TextInput
          label={t('common.slug')}
          value={effectiveSlug}
          disabled={isTranslateOnly}
          onChange={(e) => {
            setSlugTouched(true);
            setSlug(e.target.value);
          }}
          error={errors.slug}
          hint={t('pages.slugHint')}
        />
        <TextInput
          label={t('common.key')}
          required
          value={key}
          disabled={isTranslateOnly}
          onChange={(e) => setKey(e.target.value)}
          error={errors.key}
          hint={t('pages.keyHint')}
        />
      </div>
    </Card>
  );

  const tabs = [
    ...(!isNew && config.showPageSections
      ? [{ value: 'sections', label: t('pages.tabSections'), content: <SectionBuilder pageId={original.id} /> }]
      : []),
    { value: 'details', label: t('pages.tabDetails'), content: identityForm },
    {
      value: 'seo',
      label: t('pages.tabSeo'),
      content: (
        <Card>
          <SeoPanel value={seo} onChange={setSeo} translateOnly={isTranslateOnly} />
        </Card>
      ),
    },
    ...(!isNew
      ? [{ value: 'history', label: t('pages.tabHistory'), content: <RevisionsTab entityType={ENTITY_TYPE} entityId={original.id} /> }]
      : []),
  ];

  return (
    <div>
      <PageHeader
        eyebrow={t('pages.eyebrow')}
        title={isNew ? t('pages.newPage') : title.en || t('pages.untitled')}
        actions={
          <div className="flex items-center gap-sm">
            {original && <PreviewButton entityType={ENTITY_TYPE} entityId={original.id} />}
            <PermissionButton
              allowed={canWrite || isTranslateOnly}
              reason={t('pages.editPermissionRequired')}
              loading={save.isPending}
              onClick={() => save.mutate(undefined)}
            >
              {isNew ? t('common.saveDraft') : t('common.save')}
            </PermissionButton>
          </div>
        }
      />

      {original && (
        <div className="mb-lg flex flex-wrap items-center justify-between gap-md">
          <PublishControls
            status={original.status}
            scheduledAt={original.scheduled_at}
            onPublish={(at) => publish.mutate(at)}
            onUnpublish={() => unpublish.mutate(undefined)}
            busy={publish.isPending || unpublish.isPending}
          />
        </div>
      )}

      <Tabs tabs={tabs} />
    </div>
  );
}
